import { useEffect, useRef, useState, useCallback } from 'react';
import axios, { AxiosResponse } from 'axios';

interface UseAllListPagesInput<D, F> {
    filter?: F;
    getToken: GetToken;
    limit?: number;
    onError?(error: Error): void;
    onLoadAllPages?: OnLoadAllPages<D>;
    query: string;
    queryName: string;
    trqkDirectoryUrl: string;
}

type OnLoadAllPages<D> = (data: D[]) => void;

interface UseAllListPagesResult<D> {
    data: D[];
    loading: boolean;
}

export function useAllListPages<D, F>({
    filter,
    getToken,
    limit,
    onError,
    onLoadAllPages,
    query,
    queryName,
    trqkDirectoryUrl,
}: UseAllListPagesInput<D, F>): UseAllListPagesResult<D> {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<D[]>([]);
    const [nextToken, setNextToken] = useState<Maybe<string>>(null);
    const isFirstPageRef = useRef(true);
    // const { cache } = useApolloClient();

    const fetchPage = useCallback(() => {
        setLoading(true);

        getToken((token: string): void => {
            axios({
                method: 'post',
                url: trqkDirectoryUrl,
                headers: {
                    Authorization: token,
                },
                data: {
                    query,
                    variables: {
                        filter,
                        limit,
                        nextToken,
                    },
                },
            })
                .then(
                    (
                        response: AxiosResponse<{
                            data: {
                                [key: string]: {
                                    items: D[];
                                    nextToken?: Maybe<string>;
                                };
                            };
                        }>,
                    ): void => {
                        const currentItems: D[] =
                            response?.data?.data?.[queryName]?.items ?? [];
                        const currentNextToken =
                            response?.data?.data?.[queryName]?.nextToken ??
                            null;

                        if (isFirstPageRef.current) {
                            setData(currentItems);

                            // cache.writeQuery({
                            //     query: gql(query),
                            //     data: {
                            //         items: currentItems,
                            //         nextToken: currentNextToken,
                            //     },
                            // });

                            if (!currentNextToken && onLoadAllPages) {
                                onLoadAllPages(currentItems);
                            }
                        } else {
                            setData((prev: D[]): D[] => {
                                const allItems = [...prev, ...currentItems];

                                // cache.writeQuery({
                                //     query: gql(query),
                                //     data: {
                                //         items: allItems,
                                //         nextToken: currentNextToken,
                                //     },
                                // });

                                if (!currentNextToken && onLoadAllPages) {
                                    onLoadAllPages(allItems);
                                }

                                return allItems;
                            });
                        }

                        setNextToken(currentNextToken);

                        if (!currentNextToken) {
                            isFirstPageRef.current = true;
                            setLoading(false);
                        } else {
                            isFirstPageRef.current = false;
                        }
                    },
                )
                .catch((error: Error): void => {
                    if (onError) {
                        onError(error);
                    }
                });
        });
    }, [
        filter,
        limit,
        getToken,
        onError,
        queryName,
        isFirstPageRef,
        nextToken,
        // cache,
    ]);

    useEffect(() => {
        if (nextToken) {
            fetchPage();
        }
    }, [fetchPage, nextToken]);

    const filterAsString = JSON.stringify(filter);

    useEffect(() => {
        fetchPage();
    }, [filterAsString]);

    return {
        data,
        loading,
    };
}
